/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2221 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

// xng-change: Change some structures and use general structures to match the lines on the mock-ups

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { InvoiceLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Clickable from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import LineContainer, { lineStyle } from "@monsma/App/Components/LineContainer/LineContainer";
import TextOverflow from "@monsma/App/Components/TextOverflow/TextOverflow";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    invoiceLine: InvoiceLineModel;
    extendedStyles?: InvoiceDetailsLineCardStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const disableLazyLoad = parseQueryString<{ disableLazyLoad?: string }>(location.search).disableLazyLoad;

    return {
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
        showPricing: state.context.showPricing,
        disableLazyLoad: disableLazyLoad?.toLowerCase() === "true",
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface InvoiceDetailsLineCardStyles {
    container?: GridContainerProps;
    imageItem?: GridItemProps;
    imageLink?: LinkPresentationProps;
    image?: LazyImageProps;
    infoItem?: GridItemProps;
    infoInnerContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    brandItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    productNameItem?: GridItemProps;
    productNameLink?: LinkPresentationProps;
    numbersItem?: GridItemProps;
    numbersInnerContainer?: GridContainerProps;
    partItem?: GridItemProps;
    partStyles?: SmallHeadingAndTextStyles;
    myPartItem?: GridItemProps;
    myPartStyles?: SmallHeadingAndTextStyles;
    mgfItem?: GridItemProps;
    mgfGridContainer?: GridContainerProps;
    mgfStyles?: SmallHeadingAndTextStyles;
    notesItem?: GridItemProps;
    notesStyles?: SmallHeadingAndTextStyles;
    infoRightColumn?: GridItemProps;
    infoRightColumnContainer?: GridContainerProps;
    priceItem?: GridItemProps;
    priceStyles?: SmallHeadingAndTextStyles;
    qtyItem?: GridItemProps;
    qtyStyles?: SmallHeadingAndTextStyles;
    totalItem?: GridItemProps;
    totalStyles?: SmallHeadingAndTextStyles;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productDescriptionText?: TypographyPresentationProps;
    SerialNum?: TypographyProps;
    SerialNumContainer?: TypographyProps;
    SerialNumValue?: TypographyProps;
}

export const cardStyles: InvoiceDetailsLineCardStyles = {
    imageItem: {
        width: 2,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
            }
        `,
    },
    image: {
        height: "auto",
    },
    infoItem: {
        width: 10,
    },
    infoInnerContainer: {
        gap: 8,
    },
    infoLeftColumn: {
        width: [12, 12, 10, 7, 7],
        printWidth: 7,
    },
    infoLeftColumnContainer: {
        gap: 8,
    },
    brandItem: {
        width: 12,
    },
    brandStyles: {
        nameText: {
            css: css`
                color: ${getColor("text.main")};
            `,
        },
    },
    productNameItem: {
        width: 12,
    },
    numbersItem: {
        width: 12,
    },
    numbersInnerContainer: {
        gap: 8,
    },
    partItem: {
        width: [6, 6, 4, 4, 4],
        printWidth: 4,
    },
    myPartItem: {
        width: [6, 6, 4, 4, 4],
        printWidth: 4,
    },
    mgfItem: {
        width: [6, 6, 4, 4, 4],
        printWidth: 4,
    },
    notesItem: {
        width: 12,
    },
    infoRightColumn: {
        width: [12, 12, 2, 5, 5],
        printWidth: 5,
    },
    infoRightColumnContainer: {
        gap: 8,
    },
    priceItem: {
        width: [6, 6, 12, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    priceStyles: {
        text: {
            weight: "bold",
        },
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    qtyItem: {
        width: [6, 6, 12, 3, 3],
        printWidth: 3,
    },
    totalItem: {
        width: [12, 12, 12, 4, 4],
        printWidth: 4,
        css: css`
            font-weight: 600;
            flex-direction: column;
        `,
    },
    mgfGridContainer: {
        css: css`
            padding: 4px;
        `,
    },
    SerialNum: {
        size: 13,
        css: css`
            margin-right: 5px;
        `,
    },
    SerialNumContainer: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    SerialNumValue: {
        css: css`
            max-height: 22px;
        `,
    },
};

const InvoiceDetailsLineCard = ({
    invoiceLine,
    enableVat,
    vatPriceDisplay,
    showPricing,
    disableLazyLoad,
    extendedStyles,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(mergeToNew(cardStyles, extendedStyles), lineStyle));

    const isShareEntity = getIsShareEntity();

    const serials = invoiceLine.properties.serialNumber?.split(",");
    let serialDisplay = "";
    let charLimit = 0;
    if (serials) {
        if (serials.length <= 3) {
            serialDisplay = serials.join();
        } else {
            serialDisplay = serials.slice(0, 2).join().concat("...");
            for (let i = 0; i < 2; i++) {
                charLimit += serials[i].length + 2;
            }
        }
    }

    return (
        <LineContainer>
            <LineContainer.Image>
                <StyledWrapper {...(styles as any).imageWrapper}>
                    {invoiceLine.productUri && (
                        <Clickable {...(styles as any).productImage?.linkWrappingImage} href={invoiceLine.productUri}>
                            <LazyImage
                                {...(styles as any).productImage?.image}
                                src={invoiceLine.mediumImagePath}
                                altText={invoiceLine.shortDescription}
                                disableLazyLoad={disableLazyLoad}
                                isShareEntity={isShareEntity}
                            />
                        </Clickable>
                    )}
                </StyledWrapper>
            </LineContainer.Image>
            <LineContainer.Info>
                <ProductPartNumbers
                    productNumber={invoiceLine.productERPNumber}
                    customerProductNumber={invoiceLine.customerProductNumber}
                    manufacturerItem={invoiceLine.manufacturerItem}
                    showCustomerName={false} // xng-change: hide it to match the mock-ups
                    showManufacturerItem={false} // xng-change: hide it to match the mock-ups
                    extendedStyles={(styles as any).productPartNumbers}
                />
                {invoiceLine.properties.serialNumber && (
                    <StyledWrapper {...styles.SerialNumContainer}>
                        <Typography {...styles.SerialNum} as="p">
                            {translate("Serial #:")}
                        </Typography>
                        <StyledWrapper {...styles.SerialNumValue}>
                            <TextOverflow characters={charLimit} text={serials.join(",\n")} />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {invoiceLine.brand && (
                    <ProductBrand
                        brand={invoiceLine.brand}
                        showLogo={false}
                        extendedStyles={(styles as any).productBrand}
                    />
                )}
                {invoiceLine.productUri && (
                    <Link
                        {...(styles as any).productDescription?.productDetailLink}
                        href={invoiceLine.productUri}
                        data-test-selector="invoiceLine_product"
                    >
                        {invoiceLine.shortDescription || invoiceLine.description}
                    </Link>
                )}
                {!invoiceLine.productUri && (
                    <Typography as="p" data-test-selector="invoiceLine_product" {...styles.productDescriptionText}>
                        {invoiceLine.shortDescription || invoiceLine.description}
                    </Typography>
                )}
            </LineContainer.Info>
            <LineContainer.Qty>
                <SmallHeadingAndText
                    heading={translate("Invoiced")}
                    text={invoiceLine.qtyInvoiced}
                    extendedStyles={styles.qtyStyles}
                />
            </LineContainer.Qty>
            <LineContainer.Price>
                {showPricing && (
                    <>
                        <StyledWrapper {...(styles as any).priceWrapper}>
                            <Typography as="p" {...(styles as any).priceHeading}>
                                {translate("Price")}
                            </Typography>
                            <Typography as="p" {...(styles as any).priceText}>
                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? invoiceLine.unitPriceWithVatDisplay
                                    : invoiceLine.unitPriceDisplay}
                                {invoiceLine.unitOfMeasure && (
                                    <Typography {...(styles as any).priceSubText}>
                                        / {invoiceLine.unitOfMeasure}
                                    </Typography>
                                )}
                            </Typography>
                        </StyledWrapper>
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${invoiceLine.taxRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.secondaryPriceText} as="p">
                                            {invoiceLine.unitPriceDisplay +
                                                (invoiceLine.unitOfMeasure ? ` / ${invoiceLine.unitOfMeasure}` : "")}
                                        </Typography>
                                        <Typography as="p" {...styles.secondaryVatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <SmallHeadingAndText
                                heading={`${translate("Total")} (${translate("Ex. VAT")})`}
                                text={invoiceLine.lineTotalDisplay}
                                extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                            />
                        )}
                        <SmallHeadingAndText
                            heading={
                                !enableVat
                                    ? translate("Total")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                    ? `${translate("Total")} (${translate("Inc. VAT")})`
                                    : `${translate("Total")} (${translate("Ex. VAT")})`
                            }
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? invoiceLine.netPriceWithVatDisplay
                                    : invoiceLine.lineTotalDisplay
                            }
                            extendedStyles={styles.totalStyles}
                        />
                    </>
                )}
            </LineContainer.Price>
            <LineContainer.Note>
                {invoiceLine.notes && (
                    <StyledWrapper>
                        <Typography {...(styles as any)?.orderLineLabel}>{translate("Line Notes")}</Typography>
                        <Typography {...(styles as any)?.orderLineText}>{invoiceLine.notes}</Typography>
                    </StyledWrapper>
                )}
            </LineContainer.Note>
        </LineContainer>
    );
};

export default connect(mapStateToProps)(InvoiceDetailsLineCard);
